import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Button,
  Input,
  Textarea,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { LinkRegular } from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { linkSignatureRequest } from '../services/api';
import type { LinkedSignature } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxReturnId: string;
  appointmentId?: string;
  onSuccess?: (linked: LinkedSignature) => void;
}

/**
 * "Link existing Zoho Sign envelope" dialog — staff paste a Request ID (or the full admin
 * URL from Zoho's UI — backend parses the ID either way) plus an optional display name and
 * form-type hint, and the envelope's events start flowing through the webhook pipeline to
 * this tax return. Used for signatures initiated outside the app: state 8879s, Trump account
 * election forms, anything whose template isn't wired into SendForSignature.
 */
export default function LinkSignatureDialog({
  open,
  onOpenChange,
  taxReturnId,
  appointmentId,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient();

  const [requestId, setRequestId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formType, setFormType] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      linkSignatureRequest(
        taxReturnId,
        {
          requestId: requestId.trim(),
          displayName: displayName.trim() || undefined,
          formType: formType.trim() || undefined,
          notes: notes.trim() || undefined,
        },
        appointmentId,
      ),
    onSuccess: (linked) => {
      queryClient.invalidateQueries({ queryKey: ['tax-return', taxReturnId] });
      queryClient.invalidateQueries({ queryKey: ['tax-returns'] });
      reset();
      onOpenChange(false);
      onSuccess?.(linked);
    },
    onError: (err: Error) => setError(err.message),
  });

  function reset() {
    setRequestId('');
    setDisplayName('');
    setFormType('');
    setNotes('');
    setError(null);
  }

  function handleSubmit() {
    setError(null);
    if (!requestId.trim()) {
      setError('Zoho Sign Request ID is required.');
      return;
    }
    mutation.mutate();
  }

  const isSubmitting = mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!isSubmitting) onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Link existing Zoho Sign envelope</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--tl-text-secondary)' }}>
              Paste the Zoho Sign <strong>Request ID</strong> (or the full admin URL — we'll extract
              the ID). Use this for envelopes initiated outside the app (state 8879, election
              forms, etc.). Events from this envelope will flow to this tax return via the
              existing webhook; if it's already signed, we'll pull the signed PDF now.
            </div>

            <Field label="Zoho Sign Request ID or Admin URL" required>
              <Input
                value={requestId}
                onChange={(_, d) => setRequestId(d.value)}
                placeholder="e.g. 1234567890987654321 or https://sign.zoho.com/..."
                disabled={isSubmitting}
                autoFocus
              />
            </Field>

            <Field label="Display name" hint="What should this appear as on the tax return? e.g., 'FL State 8879'">
              <Input
                value={displayName}
                onChange={(_, d) => setDisplayName(d.value)}
                placeholder="Defaults to the envelope's Zoho name"
                disabled={isSubmitting}
              />
            </Field>

            <Field
              label="Form type"
              hint="Use a federal code (8879, 8879-C, 8879-S, 8879-PE) and signing this envelope will advance the federal return's status the same as a primary signature. Free-form values (e.g. '8879-state', '529-election') are tracked but don't gate e-file."
            >
              <Input
                value={formType}
                onChange={(_, d) => setFormType(d.value)}
                disabled={isSubmitting}
                placeholder="e.g. 8879-PE, 8879-state, 529-election"
              />
            </Field>

            <Field label="Notes">
              <Textarea
                value={notes}
                onChange={(_, d) => setNotes(d.value)}
                rows={2}
                disabled={isSubmitting}
              />
            </Field>

            {error && (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={isSubmitting ? <Spinner size="tiny" /> : <LinkRegular />}
              onClick={handleSubmit}
              disabled={isSubmitting || !requestId.trim()}
            >
              {isSubmitting ? 'Linking…' : 'Link envelope'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
